import React, { useState, useEffect, useRef } from 'react';
import PageNavi from '../PageNavi';
import TextFilter from '../Filters/TextFilter';
import DropdownFilter from '../Filters/DropdownFilter';
import ResultsOnPage from '../ResultsOnPage';
import ExportButton from '../ExportButton';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, ConstrainMode, IconButton, Stack, Spinner, SpinnerSize, Link, Label } from '@fluentui/react';
import Api from '../../../services/Api';
import axios from 'axios';
import { createAndDownloadCsv, mapEnumToFilterValues, renderDate } from '../../../helpers/HelperMethods';
import { PowerAppEnvironmentType, PowerAppEnvironmentUserRole } from '../../../GlobalConst/globalEnums';
import { useSelector } from 'react-redux';

const CancelToken = axios.CancelToken;

const EnvironmentsRolesList = ({filterBy}) => {

    const lastEnvUserSyncDate = renderDate(useSelector(state => state.app.appConfiguration.ppEnvironmentUserLastSyncDate));

    const apiAddress = '/EnvironmentsRolesPanel';

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [environments, setEnvironments] = useState([]);
    const [orderBy, setOrderBy] = useState({ fieldName: '', isDescending: false});
    const [totalResults, setTotalResults] = useState(0);
    const [onPage, setOnPage] = useState(20);
    const [pageUrlFilter, setPageUrlFilter] = useState(1);
    const [filters, setFilters] = useState({});
    const cancelSource = useRef(null);

    useEffect(() => {
        return getEnvironments();
    }, [onPage, pageUrlFilter, filters, orderBy])

    const _onColumnClick = (ev, column) => {
        setOrderBy({
            fieldName: column.fieldName, 
            isDescending: !column.isSortedDescending
        });
    }

    const initialColumns: IColumn[] = [
        {
            key: 'displayName',
            name: 'Name',
            fieldName: 'displayName',
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <span>{item.user.displayName}</span>}
        },
        {
            key: 'email',
            name: 'Email',
            fieldName: 'email',
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <span>{item.user.email}</span>}
        },
        {
            key: 'name',
            name: 'Environment Name',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <Link href={item.environment.url} target='_blank'>{item.environment.name}</Link>}
        },
        {
            key: 'url',
            name: 'Environment Dataverse URL',
            fieldName: 'dataverseUrl',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <Link href={item.environment.dataverseUrl} target='_blank'>{item.environment.dataverseUrl}</Link>}
        },
        {
            key: 'author',
            name: 'Environment Owner',
            fieldName: 'owner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <span>{item.environment.owner && (item.environment.owner.email != null ? item.environment.owner.email : item.environment.owner.displayName)}</span>}
        },
        {
            key: 'created',
            name: 'Environment Creation date',
            fieldName: 'created',
            minWidth: 150,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'number',
            onRender: (item) => { return <span>{renderDate(item.environment.created)}</span>;}
        },
        {
            key: 'type',
            name: 'Environment Type',
            fieldName: 'type',
            minWidth: 150,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <span>{item.environment.type}</span>}
        },
        {
            key: 'role',
            name: 'Role in environment',
            fieldName: 'role',
            minWidth: 75,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => { return <span>{item.role}</span>}
        },
    ] 

    const getColumns = (initialColumns) => {
 
        const columns = initialColumns.map(c => {
            c.isFiltered = (filters[c.fieldName] || '').length > 0;
            c.isSorted = orderBy.fieldName === c.fieldName;
            c.isSortedDescending = c.isSorted ? orderBy.isDescending : true;
            return c;
        });

        return columns;
    }

    const onFilterChange = (fieldName, value) => {
        setFilters({
            ...filters,
            [fieldName]: value
        });
        setPageUrlFilter(1);
    }

    const loadData = (envs) => {
        setEnvironments(envs);
        setIsLoading(false);
    }

    const handleNoSelection = () => {
        loadData([]);
        setTotalResults(0);
    }

    const getResultsUrl = (isExport) => {
        let url = `${apiAddress}?`
        
        if (isExport)
            url += `forExport=1`;
        else
            url += `page=${pageUrlFilter}&onPage=${onPage}`;

        const filterUrl = Object.keys(filters).reduce((queryString, filterField) => {
            const filterText = filters[filterField];
            if(filterText) {
                queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
            }
            return queryString;
        }, '');

        let orderByUrl = '';
        if(orderBy.fieldName) {
            orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
        }

        url = url + filterUrl + orderByUrl;

        if (filterBy) {
            url += `&filterBy=${filterBy}`
        }

        return url
    }

    const getEnvironments = () => {
        setIsLoading(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(false), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            loadData(posts.results);
            setTotalResults(posts.totalCount);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }
    }

    const exportFields = [
    {
        label: 'Username',
        value: 'displayName'
    },{
        label: 'Email',
        value: 'email'
    },{
        label: 'Name',
        value: 'name'
    },{
        label: 'Dataverse URL',
        value: 'dataverseUrl'
    },{
        label: 'Owner',
        value: 'owner.email'
    },{
        label: 'Created',
        value: 'created'
    },{
        label: 'Type',
        value: 'type'
    },{
        label: 'Role',
        value: 'role'
    },];

    const exportData = () => {
        setIsExporting(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(true), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            createAndDownloadCsv(posts, "environmentsRoles", exportFields);
            setIsExporting(false);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }     
    }

    const columns = getColumns(initialColumns);
    const spinner = <Spinner style={{margin: '200px'}} size={SpinnerSize.large} />;

    const lastSyncDate = lastEnvUserSyncDate && 
        <Label style={{position:'absolute', top: 35, right: 5}}>Last refresh date: {lastEnvUserSyncDate}</Label>

    return( 
        <div>
            <div>
                    <div className="filters">
                    <div className="cell">
                            <TextFilter
                                fieldName='users'
                                fieldLabel='User'
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="cell">
                            <TextFilter
                                fieldName='name'
                                fieldLabel='Environment name'
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="cell">
                            <TextFilter
                                fieldName='url'
                                fieldLabel='Environment URL'
                                onChange={onFilterChange}
                            />
                        </div>        
                        <div className="cell">
                            <TextFilter
                                fieldName='owner'
                                fieldLabel='Environment Owner'
                                onChange={onFilterChange}
                            />
                        </div>          
                        <div className="cell">
                            <DropdownFilter
                                fieldName="type"
                                title='Environment Type'
                                onChange = {onFilterChange}
                                noselection = {handleNoSelection}
                                includeSelectAll = {true}
                                values={mapEnumToFilterValues(PowerAppEnvironmentType).filter(x => x.value !== PowerAppEnvironmentType.teams)}
                            />
                        </div>          
                        <div className="cell">
                            <DropdownFilter
                                fieldName="role"
                                title='Environment Role'
                                onChange = {onFilterChange}
                                noselection = {handleNoSelection}
                                includeSelectAll = {true}
                                values={mapEnumToFilterValues(PowerAppEnvironmentUserRole)}
                            />
                        </div>
                    </div>
                        <div style={{position:'relative', margin: '20px 0 40px'}}>
                            <div>
                                <IconButton 
                                    style={{color: 'rgb(96, 94, 92)', position: 'absolute'}} 
                                    onClick={getEnvironments}
                                    iconProps={{iconName: 'refresh'}} 
                                />
                                <PageNavi
                                    pageNumPart = {setPageUrlFilter}
                                    totalResults = {totalResults}
                                    resultsOnPage = {onPage}
                                    pageNum = {pageUrlFilter}
                                    showTotalResults = {true}
                                />
                                <Stack horizontal disableShrink>
                                    {lastSyncDate}
                                    <ResultsOnPage
                                        onPage = {onPage}
                                        onPageResults = {setOnPage}
                                    />
                                    <ExportButton 
                                        onClick = {exportData}
                                        loading = {isExporting}
                                    />
                                </Stack>                            
                            </div>
                        </div>
            </div>
            <div>
                {
                    isLoading ?
                    spinner 
                    : <DetailsList
                        items={environments}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        selectionMode={SelectionMode.none}
                        styles={{root: {width: '100%'}}}
                        onColumnHeaderClick={_onColumnClick}
                        constrainMode={ConstrainMode.unconstrained}
                    />
                }
            </div>
        </div>
    )
}

export default EnvironmentsRolesList;


// WEBPACK FOOTER //
// ./src/components/Administration/PowerPlatform/EnvironmentsRolesList.js